import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  HeartHandshake,
  HandHeart,
  Building2,
  Users,
  Stethoscope,
  Sparkles,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PUBLIC_GET_INVOLVED } from "../constants/testIds";

const pathways = [
  {
    id: "buddy",
    icon: HeartHandshake,
    tag: "SURVIVORS & CARERS",
    title: "Become a Buddy",
    desc: "Support someone newly diagnosed by walking alongside them. Trained BTC Buddies offer lived-experience companionship, not clinical advice.",
    ctaLabel: "Apply to be a Buddy",
    ctaTo: "/buddy/apply",
    ctaTestId: PUBLIC_GET_INVOLVED.buddyCta,
    accent: "#23803F",
  },
  {
    id: "volunteer",
    icon: HandHeart,
    tag: "COMMUNITY",
    title: "Volunteer with BTC",
    desc: "From peer-support helpers to event hosts, resource researchers and community welcomers — BTC is powered by volunteers who lend their time and heart.",
    ctaLabel: "Volunteer with us",
    ctaTo: "/volunteer/apply",
    ctaTestId: PUBLIC_GET_INVOLVED.volunteerCta,
    accent: "#1A5FAB",
  },
  {
    id: "professionals",
    icon: Sparkles,
    tag: "PROFESSIONALS",
    title: "Trainers, coaches, therapists, wellness pros",
    desc: "Personal trainers, coaches, therapists and wellness practitioners can register interest to support BTC members. We onboard partners carefully and in stages.",
    ctaLabel: "Register interest",
    ctaTo: "/contact",
    accent: "#1F5D5B",
  },
  {
    id: "hospitals",
    icon: Stethoscope,
    tag: "HOSPITAL TRUSTS",
    title: "Hospital & trust partnerships",
    desc: "Extend patient support beyond the clinic. BTC works alongside hospital trusts and cancer centres to make the space between appointments feel less alone.",
    ctaLabel: "Talk to our team",
    ctaTo: "/contact",
    ctaTestId: PUBLIC_GET_INVOLVED.partnerCta,
    accent: "#1A5FAB",
  },
  {
    id: "orgs",
    icon: Users,
    tag: "PARTNERS & ORGS",
    title: "Charity & organisation partners",
    desc: "Charities, community services and mission-aligned organisations can partner with BTC to reach and support more people, together.",
    ctaLabel: "Explore partnering",
    ctaTo: "/organization/apply",
    accent: "#2D9B4E",
  },
  {
    id: "donors",
    icon: Building2,
    tag: "SUPPORTERS",
    title: "Donate to keep BTC free",
    desc: "BTC support is free for the people who need it. Donations keep the community running, resources current and the doors open to every new patient.",
    ctaLabel: "Donate to BTC",
    ctaTo: "/donate",
    ctaTestId: PUBLIC_GET_INVOLVED.donateCta,
    accent: "#2D9B4E",
  },
];

export default function GetInvolved() {
  return (
    <div data-testid={PUBLIC_GET_INVOLVED.page} className="bg-white">
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section
          data-testid={PUBLIC_GET_INVOLVED.hero}
          className="pt-28 lg:pt-32 pb-14 lg:pb-20 bg-white"
        >
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12">
            <p className="btc-eyebrow mb-4">GET INVOLVED</p>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-[40px] sm:text-[52px] lg:text-[64px] text-[#0B132B] mb-6">
              How can you <span className="text-[#23803F]">help BTC?</span>
            </h1>
            <div className="h-[3px] w-16 bg-[#2D9B4E] rounded-full mb-6" />
            <p className="text-lg sm:text-xl text-[#4B5563] leading-relaxed max-w-3xl">
              BTC is powered by people. Whether you have lived experience, professional skill, an
              hour a week or a hospital ward to represent — there is a way to walk with us. Choose
              the path that fits you.
            </p>
          </div>
        </section>

        {/* Pathways grid */}
        <section className="bg-[#F8F9FB] py-16 lg:py-24">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            {pathways.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={PUBLIC_GET_INVOLVED.pathwayCard}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 2) * 0.05 }}
                className="bg-white rounded-2xl border border-gray-100 p-7 lg:p-8 flex flex-col"
              >
                <div className="flex items-start gap-4 mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${p.accent}14` }}
                  >
                    <p.icon className="w-6 h-6" strokeWidth={1.5} style={{ color: p.accent }} />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] mb-1" style={{ color: p.accent }}>
                      {p.tag}
                    </div>
                    <h2 className="text-xl font-bold text-[#0B132B] leading-snug">{p.title}</h2>
                  </div>
                </div>
                <p className="text-[15px] text-[#4B5563] leading-relaxed mb-6 flex-1">{p.desc}</p>
                <Link
                  to={p.ctaTo}
                  data-testid={p.ctaTestId}
                  className="text-[13px] font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                  style={{ color: p.accent }}
                >
                  {p.ctaLabel} <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Public vs private note */}
        <section className="bg-white py-16 lg:py-20">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-12">
            <div className="rounded-2xl border border-gray-100 bg-[#F8F9FB] p-8 lg:p-10">
              <p className="btc-eyebrow mb-3">A NOTE ON BTC</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B132B] tracking-tight mb-4">
                Are you looking for support?
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-6">
                Get Involved is for people who want to help BTC. If you are a patient, carer or
                family member looking for support for yourself or a loved one, the fastest way to
                reach us is through Get Support.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/support" className="btc-btn-primary justify-center">
                  GET SUPPORT
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/about" className="btc-btn-outline justify-center">
                  Learn about BTC
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
